import React from "react";

import Radio from "@material-ui/core/Radio";
import { Collapse, TextInput, SelectInput, MenuItem } from "@portex-pro/ui-components";
import { LtlTrailerType } from "api/graphql/generated";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isTemperatureControlled, TrailerTypeEnum } from "types/TrailerType";
import { formatTrailerType } from "utils/formatTrailerType";

const trailerTypesList = Object.values(LtlTrailerType);

export const AdditionalInfoSection: React.FC = () => {
  const { t } = useTranslation(["common", "quoteRequest"]);
  const { control, watch } = useFormContext();

  const is_hazardous = watch("is_hazardous");
  const trailer_type = watch("trailer_type");

  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();

  return (
    <section className="mb-6 max-w-[70%]">
      <h3 className="text-md text-gray-500 font-semibold mb-2">
        {t("quoteRequest:CargoStep.AdditionalInfoSection.title")}
      </h3>
      <div className="flex flex-col gap-4 w-full">
        <div className="flex-1 flex items-start gap-4">
          {/* Trailer Type Select */}
          <div className="flex-1">
            <label className="block text-sm font-bold mb-1">
              {t("quoteRequest:CargoStep.AdditionalInfoSection.trailerTypeLabel")}
            </label>
            <Controller
              name="trailer_type"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <SelectInput
                    value={field.value ?? ""}
                    onChange={(e) =>
                      setQuoteRequestAction({ load_spec: { trailer_type: e.target.value as TrailerTypeEnum } })
                    }
                    fullWidth
                    displayEmpty
                    error={fieldState.invalid}
                  >
                    {trailerTypesList.map((item) => (
                      <MenuItem key={item} value={item}>
                        {formatTrailerType(item)}
                      </MenuItem>
                    ))}
                  </SelectInput>
                  {fieldState.error && (
                    <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                  )}
                </>
              )}
            />
          </div>
          {/* Hazardous Radio */}
          <div>
            <label className="block text-sm font-bold mb-1">
              {t("quoteRequest:CargoStep.AdditionalInfoSection.hazardousGoodsLabel")}
            </label>
            <div className="flex gap-2">
              <Controller
                name="is_hazardous"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <label className="flex items-center">
                      <Radio
                        checked={field.value === false}
                        onChange={() => setQuoteRequestAction({ is_hazardous: false })}
                        value="false"
                        color="primary"
                      />
                      {t("common:no")}
                    </label>
                    <label className="flex items-center">
                      <Radio
                        checked={field.value === true}
                        onChange={() => setQuoteRequestAction({ is_hazardous: true })}
                        value="true"
                        color="primary"
                      />
                      {t("common:yes")}
                    </label>
                    {fieldState.error && (
                      <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          {/* Goods Value */}
          <div className="flex-1">
            <label className="block text-sm font-bold mb-1">
              {t("quoteRequest:CargoStep.AdditionalInfoSection.goodsValueLabel")}
            </label>
            <Controller
              name="goods_value"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <TextInput
                    type="number"
                    value={field.value}
                    onChange={(e) => setQuoteRequestAction({ goods_value: Number(e.target.value) })}
                    fullWidth
                    startIcon={<span className="ml-1">$</span>}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                </>
              )}
            />
          </div>
        </div>

        {/* Hazardous Details */}
        <Collapse in={is_hazardous === true}>
          <Controller
            name="hazardous_goods_details"
            control={control}
            render={({ field }) => (
              <TextInput
                margin="normal"
                label={t("quoteRequest:CargoStep.AdditionalInfoSection.hazardousDetailsLabel")}
                required
                fullWidth
                multiline
                rows={3}
                placeholder={t("quoteRequest:CargoStep.AdditionalInfoSection.hazardousDetailsPlaceholder")}
                value={field.value}
                onChange={(e) => setQuoteRequestAction({ hazardous_goods_details: e.target.value })}
              />
            )}
          />
        </Collapse>

        {/* Temperature Controlled */}
        <Collapse in={isTemperatureControlled(trailer_type)}>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-bold mb-1">
                {t("quoteRequest:CargoStep.AdditionalInfoSection.minTempLabel")}
              </label>
              <Controller
                name="min_temp"
                control={control}
                render={({ field }) => (
                  <TextInput
                    type="number"
                    value={field.value}
                    onChange={(e) => setQuoteRequestAction({ load_spec: { min_temp: Number(e.target.value) } })}
                    fullWidth
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-bold mb-1">
                {t("quoteRequest:CargoStep.AdditionalInfoSection.maxTempLabel")}
              </label>
              <Controller
                name="max_temp"
                control={control}
                render={({ field }) => (
                  <TextInput
                    type="number"
                    value={field.value}
                    onChange={(e) => setQuoteRequestAction({ load_spec: { max_temp: Number(e.target.value) } })}
                    fullWidth
                  />
                )}
              />
            </div>
          </div>
        </Collapse>

        {/* Additional Notes */}
        <div>
          <label className="block text-sm font-bold ">
            {t("quoteRequest:CargoStep.AdditionalInfoSection.additionalNotesLabel")}
          </label>
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextInput
                rows={3}
                multiline
                margin="normal"
                fullWidth
                value={field.value}
                onChange={(e) => setQuoteRequestAction({ note: e.target.value })}
              />
            )}
          />
        </div>
      </div>
    </section>
  );
};
